import { zodResolver } from '@hookform/resolvers/zod';
import { TextField } from '@mui/material';
import {
  UpdateInvoicesAdditionalDetailsRequestSchema,
  type UpdateInvoicesAdditionalDetailsRequestPayload,
} from '@vy/api-definition';
import { isNotNullOrUndefined, isSingleItem } from '@vy/utils';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { VyDialog } from '../../../../../../components/new-design/feedback/VyDialog';
import { useBulkUpdateInvoiceAdditionalDetails } from '../../../../../../hooks/requests/use-bulk-update-invoice-additional-details';
import { useUpdateInvoiceAdditionalDetails } from '../../../../../../hooks/requests/use-update-invoice-additional-details';
import { queryKeys } from '../../../../../../services/query-key/query-keys';
import { runMutation } from '../../../../../../utils/vy-mutation';

type AddInvoicePurchaseOrderFormProps = {
  open: boolean;
  onClose?: () => void;
  existingPurchaseOrder?: string;
  invoiceIds: string[];
};

export const AddInvoicePurchaseOrderForm = ({
  open,
  onClose,
  existingPurchaseOrder,
  invoiceIds,
}: AddInvoicePurchaseOrderFormProps) => {
  const isSingleInvoice = isSingleItem(invoiceIds);
  const invoiceId = invoiceIds[0];

  const updatePurchaseOrderForm = useForm<UpdateInvoicesAdditionalDetailsRequestPayload>({
    resolver: zodResolver(UpdateInvoicesAdditionalDetailsRequestSchema),
  });

  useEffect(() => {
    if (isSingleInvoice && isNotNullOrUndefined(existingPurchaseOrder)) {
      updatePurchaseOrderForm.reset({ purchaseOrder: existingPurchaseOrder, invoiceIds });
    } else {
      updatePurchaseOrderForm.reset({ purchaseOrder: undefined, invoiceIds });
    }
  }, [isSingleInvoice, existingPurchaseOrder, invoiceId]);

  const purchaseOrderInputRegister = updatePurchaseOrderForm.register('purchaseOrder');

  const queryClient = useQueryClient();
  const updateInvoiceAdditionalDetailsMutation = useUpdateInvoiceAdditionalDetails();
  const bulkUpdateInvoiceAdditionalDetailsMutation = useBulkUpdateInvoiceAdditionalDetails();

  const onSubmit = updatePurchaseOrderForm.handleSubmit(async ({ purchaseOrder }) => {
    if (isSingleInvoice) {
      await runMutation(
        updateInvoiceAdditionalDetailsMutation,
        { id: invoiceId, purchaseOrder },
        {
          pending: 'Updating purchase order...',
          success: 'Purchase order updated',
          error: 'Failed to update purchase order',
        },
        {
          onSuccess: async () => {
            await Promise.all([
              queryClient.invalidateQueries(queryKeys.invoicePdf(invoiceId)),
              queryClient.invalidateQueries(queryKeys.dynamicInvoice(invoiceId)),
            ]);
          },
        },
      );
    } else {
      await runMutation(
        bulkUpdateInvoiceAdditionalDetailsMutation,
        { invoiceIds, purchaseOrder },
        {
          pending: 'Updating purchase order...',
          success: 'Purchase order updated',
          error: 'Failed to update purchase order',
        },
      );
    }

    updatePurchaseOrderForm.reset();
    return onClose?.();
  });

  const handleClose = () => {
    updatePurchaseOrderForm.reset();
    updatePurchaseOrderForm.clearErrors();
    onClose?.();
  };

  return (
    <VyDialog
      open={open}
      onClose={handleClose}
      label="Add purchase order number"
      labelDescription="The PO number will be added to this invoice only"
      onSubmit={onSubmit}
      ctaText="Update"
      cancelButtonText="Cancel"
    >
      <TextField
        {...purchaseOrderInputRegister}
        label="PO number"
        placeholder="Text here"
        type="text"
        size="medium"
        fullWidth
        sx={{
          padding: '15px',
        }}
        error={isNotNullOrUndefined(
          updatePurchaseOrderForm.formState.errors[purchaseOrderInputRegister.name],
        )}
        helperText={updatePurchaseOrderForm.formState.errors[purchaseOrderInputRegister.name]?.message}
      />
    </VyDialog>
  );
};
